import React, { useRef, useState, useEffect, useCallback } from "react";
import { useImageCache } from "@/app/hooks/useImageCache";
import { throttle } from "lodash";

interface CarRotationProps {
  spriteUrl: string;
  totalFrames: number;
}

const CarRotation = ({ spriteUrl, totalFrames }: CarRotationProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);
  const [sprite, setSprite] = useState<HTMLImageElement | null>(null);
  const { image, isLoading } = useImageCache(spriteUrl);
  const frameRef = useRef<number>(0);
  const rafRef = useRef<number>();

  // Ao trocar o spriteUrl, reseta o sprite para forçar o carregamento,
  // mas mantém o currentFrame inalterado para preservar a posição.
  useEffect(() => {
    setSprite(null);
  }, [spriteUrl]);

  useEffect(() => {
    if (image) setSprite(image);
  }, [image]);

  const drawFrame = useCallback(
    (frame: number, spriteImage: HTMLImageElement) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const dpr = window.devicePixelRatio || 1;
      const containerWidth = canvas.width / dpr;
      const containerHeight = canvas.height / dpr;
      ctx.clearRect(0, 0, containerWidth, containerHeight);

      const frameWidth = spriteImage.width / totalFrames;
      const frameHeight = spriteImage.height;
      const scale = Math.min(
        containerWidth / frameWidth,
        containerHeight / frameHeight
      );
      const scaledWidth = frameWidth * scale;
      const scaledHeight = frameHeight * scale;
      const x = (containerWidth - scaledWidth) / 2;
      const y = (containerHeight - scaledHeight) / 2;

      ctx.drawImage(
        spriteImage,
        frameWidth * frame,
        0,
        frameWidth,
        frameHeight,
        x,
        y,
        scaledWidth,
        scaledHeight
      );
    },
    [totalFrames]
  );

  useEffect(() => {
    if (sprite) drawFrame(currentFrame, sprite);
  }, [sprite, currentFrame, drawFrame]);

  useEffect(() => {
    const updateCanvasSize = () => {
      const container = containerRef.current;
      const canvas = canvasRef.current;
      if (!container || !canvas) return;

      const dpr = window.devicePixelRatio || 1;
      const containerWidth = container.clientWidth;
      const aspectRatio = 16 / 9;
      const containerHeight = containerWidth / aspectRatio;

      canvas.style.width = `${containerWidth}px`;
      canvas.style.height = `${containerHeight}px`;
      canvas.width = containerWidth * dpr;
      canvas.height = containerHeight * dpr;

      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
      }
      if (sprite) drawFrame(currentFrame, sprite);
    };

    updateCanvasSize();
    window.addEventListener("resize", updateCanvasSize);
    return () => window.removeEventListener("resize", updateCanvasSize);
  }, [sprite, currentFrame, drawFrame]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setDragging(true);
    setStartX(e.clientX);
  };

  const handleMouseMove = useCallback(
    throttle((e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!dragging) return;
      const deltaX = e.clientX - startX;
      const sensitivity = 70;
      const framesToMove = Math.round(deltaX / sensitivity);
      frameRef.current =
        (((currentFrame + framesToMove) % totalFrames) + totalFrames) %
        totalFrames;

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setCurrentFrame(frameRef.current);
      });
    }, 16),
    [dragging, startX, currentFrame, totalFrames]
  );

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    setDragging(true);
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!dragging) return;
    const deltaX = e.touches[0].clientX - touchStartX;
    const sensitivity = 5;
    const framesToMove = Math.round(deltaX / sensitivity);
    const newFrame =
      (((currentFrame + framesToMove) % totalFrames) + totalFrames) %
      totalFrames;
    setCurrentFrame(newFrame);
    setTouchStartX(e.touches[0].clientX);
  };

  return (
    <div ref={containerRef} className="w-full relative">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600" />
        </div>
      )}
      <canvas
        className="cursor-grab select-none"
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={() => setDragging(false)}
        onMouseLeave={() => setDragging(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={() => setDragging(false)}
        style={{ width: "100%", height: "auto", touchAction: "none" }}
      />
      <div className="flex justify-center items-center mt-4 text-sm text-gray-600">
        <p>Segure e Arraste para girar</p>
      </div>
    </div>
  );
};

export default CarRotation;
